import { themeListAtom } from "@/shared/atoms";
import useFullScreen from "@/shared/atoms/useFullScreen";
import { useSetLayout } from "@/shared/atoms/useLayout";
import useKioskSettingsQuery from "@/shared/queries/kioskSettings/useKioskSettingsQuery";
import { setKioskModeSafe } from "@/shared/utils/electronKioskMode";
import { IThemeItem } from "@/shared/utils/interfaces";
import { useAtom } from "jotai";
import { useEffect, useMemo, type CSSProperties, type ReactNode } from "react";

function themeToCssVariables(theme: IThemeItem["theme"]): CSSProperties {
  const vars: Record<string, string> = {};
  for (const [key, value] of Object.entries(theme)) {
    if (value != null && String(value).trim() !== "") {
      vars[`--${key}`] = String(value);
    }
  }
  return vars as CSSProperties;
}

export function KioskThemeWrapper({ children }: { children: ReactNode }) {
  const setLayout = useSetLayout();
  const [_, setFullScreen] = useFullScreen();

  const { data } = useKioskSettingsQuery();
  const theme_template_id = data?.theme_template_id;
  const [themes] = useAtom(themeListAtom);

  const theme = useMemo(() => {
    if (theme_template_id == null || !themes?.length) return undefined;
    return themes.find((t) => t.item_id === theme_template_id);
  }, [themes, theme_template_id]);

  const themeStyle = useMemo(() => {
    if (!theme?.theme) return undefined;
    const vars = themeToCssVariables(theme.theme);
    const text = theme.theme.background_labels_text_color;
    const primary = theme.theme.button_background_color
    const accent = theme.theme.button_text_color
    return {
      ...vars,
      ...(text ? { "--text": text, } : {}),
      ...(primary ? { "--primary": primary,"--color-primary": primary ,"--color-primary-dark": primary} : {}),
      ...(accent ? { "--button_text_color": accent, } : {}),
    } as Record<string, string>;
  }, [theme]);

  useEffect(() => {
    const root = document.getElementById("root");
    if (!root || !theme?.theme || !themeStyle) return;

    const previous = new Map<string, string>();

    const setCustomProp = (name: string, value: string) => {
      previous.set(name, root.style.getPropertyValue(name));
      root.style.setProperty(name, value);
    };

    for (const [key, value] of Object.entries(themeStyle)) {
      if (value != null && String(value).trim() !== "") {
        setCustomProp(key, String(value));
      }
    }

    const bg = theme.theme.background_color;
    const textColor = theme.theme.background_labels_text_color;

    if (bg) {
      previous.set("background", root.style.background);
      root.style.background = bg;
    }
    if (textColor) {
      previous.set("color", root.style.color);
      root.style.color = textColor;
    }

    return () => {
      if (window.location.pathname.startsWith("/kiosk")) return;
      for (const [prop, val] of previous) {
        if (prop === "background") {
          root.style.background = val;
        } else if (prop === "color") {
          root.style.color = val;
        } else if (val === "") {
          root.style.removeProperty(prop);
        } else {
          root.style.setProperty(prop, val);
        }
      }
    };
  }, [theme, themeStyle]);


  useEffect(() => {
    return () => {
      if (window.location.pathname.startsWith("/kiosk")) return;
      const root = document.getElementById("root");
      setLayout({ mt: "mt-4", showFullscreen: true });
      setFullScreen(false);
      setKioskModeSafe(false);
      if (!root || !theme?.theme || !themeStyle) return;
      root.style = "";
    };
  }, []);
  return <>{children}</>;
}
